<template>
    <div class="navigation-bar container-indentation" data-test="navigation-bar">
        <div class="navigation-logo">
            <router-link to="/" class="navigation-logo">
                <picture v-if="isPerformax">
                    <source media="(max-width: 1499px)" srcset="@/assets/performax-small.svg" />
                    <img data-test="logo" src="@/assets/performax.svg" alt="Performax" />
                </picture>
                <img v-else data-test="logo" src="@/assets/let-there-be-ads-logo.svg" alt="Let There Be Ads" />
            </router-link>
        </div>
        <div class="navigation-menu" v-if="$mq.min.desktop">
            <template v-for="item in mainMenuItems" :key="item.routeName">
                <router-link
                    v-if="item.routeDef"
                    class="navigation-menu-item"
                    :class="{
                        'super-admin': item.superAdmin,
                        'router-link-active': item.routeName === 'dashboard-redirect' && isDashboard,
                        label: item.routeName == 'adstxt' && adsTxt != 'Ok',
                        warning: item.routeName == 'adstxt' && adsTxt == 'Duplicate',
                        error: item.routeName == 'adstxt' && adsTxt == 'Missing',
                    }"
                    data-test="menu-item"
                    :name="item.routeName"
                    :to="item.routeDef"
                >
                    {{ item.label }}
                </router-link>
                <router-link
                    v-else-if="item.routeName"
                    class="navigation-menu-item"
                    :class="{ 'super-admin': item.superAdmin }"
                    data-test="menu-item"
                    :name="item.routeName"
                    :to="{ name: item.routeName }"
                >
                    {{ item.label }}
                </router-link>
            </template>
        </div>

        <div class="navigation-right">
            <DashboardModeToggle v-if="showDashboardToggle" class="navigation-right-item" />
            <YieldPublisherPicker
                v-if="dashboardMode === 'yield'"
                class="navigation-right-item"
                data-cy="select-publisher"
            />
            <LtbaPublisherPicker v-else class="navigation-right-item" data-cy="select-publisher" />

            <div class="navigation-dropdown navigation-right-item">
                <NavigationDropdown
                    :items="!$mq.min.desktop ? [...mainMenuItems, ...accountMenuItems] : accountMenuItems"
                >
                    <template #trigger v-if="$mq.max.tablet">
                        <LaBaseIcon class="navigation-dropdown-icon" name="menu" />
                    </template>
                </NavigationDropdown>
            </div>
        </div>
    </div>
</template>

<script>
import { computed } from "vue";
import { useRoute } from "vue-router";
import NavigationDropdown from "@/components/layout/NavigationDropdown.vue";
import DashboardModeToggle from "@/components/dashboard/DashboardModeToggle.vue";
import store from "@/store/store";
import { getterTypes } from "@/store/LoggedUser/types";
import { getterTypes as publisherGetterTypes } from "@/store/Publisher/types";
import { ACCOUNT_MENU_ITEMS, MAIN_MENU_ITEMS } from "@/helpers/data";
import LaBaseIcon from "@/components/BaseIcon";
import LtbaPublisherPicker from "@/components/filters/LtbaPublisherPicker";
import YieldPublisherPicker from "@/components/filters/YieldPublisherPicker";
import { routeParamsApplicator } from "@/helpers/links";
import { getterTypes as adstxtGetterTypes } from "@/store/Adstxt/types";
import { isPerformax } from "@/helpers/variant";

export default {
    name: "LaNavigationBar",
    components: {
        LaBaseIcon,
        NavigationDropdown,
        DashboardModeToggle,
        LtbaPublisherPicker,
        YieldPublisherPicker,
    },
    setup: () => {
        const route = useRoute();
        const userRights = computed(() => store.getters[getterTypes.getUserRights]);
        const isSuperAdmin = computed(() => userRights.value && userRights.value["super_admin"]);

        const dashboardMode = computed(() => store.getters[publisherGetterTypes.getSelectedMode]);

        // Used by the Dashboard menu item to stay router-link-active across redirects —
        // its target is `dashboard-redirect`, but the effective route.name will be
        // `dashboard-ltba` or `dashboard-yield` after the shim runs.
        const isDashboard = computed(() =>
            ["dashboard-redirect", "dashboard-ltba", "dashboard-yield"].includes(route.name)
        );

        const publisherId = computed(() => {
            // For Yield dashboard, get first selected publisher ID
            if (dashboardMode.value === "yield") {
                const selectedPublishers = store.getters[publisherGetterTypes.getSelectedPublishersYield];
                return selectedPublishers && selectedPublishers.length > 0 ? selectedPublishers[0].id : null;
            }
            // For LTBA dashboard
            if (dashboardMode.value === "ltba") {
                const selectedPublisher = store.getters[publisherGetterTypes.getSelectedPublisherLtba];
                return selectedPublisher ? selectedPublisher.id : null;
            }
            // For other pages, use default
            return store.getters[publisherGetterTypes.getSelectedPublisher];
        });

        const publisher = computed(() => {
            // For Yield dashboard
            if (dashboardMode.value === "yield") {
                const selectedPublishers = store.getters[publisherGetterTypes.getSelectedPublishersYield];
                return selectedPublishers && selectedPublishers.length > 0 ? selectedPublishers[0] : null;
            }
            // For LTBA dashboard
            if (dashboardMode.value === "ltba") {
                return store.getters[publisherGetterTypes.getSelectedPublisherLtba];
            }
            // For other pages
            return store.getters[publisherGetterTypes.getPublisher];
        });

        const accountMenuItems = computed(() => {
            // Without a publisher, still show items that don't depend on one (e.g. Sign out,
            // Change Password). Otherwise the user has no way to log out when LTBA state
            // ends up empty (e.g. after toggling from yield without an LTBA seed).
            const items = publisherId.value
                ? ACCOUNT_MENU_ITEMS
                : ACCOUNT_MENU_ITEMS.filter((it) => !it.routeParams?.publisher);
            return items.map(routeParamsApplicator({ publisher: publisherId.value }));
        });
        const mainMenuItems = computed(() => {
            if (!publisherId.value) {
                return [];
            }

            let mi = MAIN_MENU_ITEMS.filter((item) => {
                // Filter by super admin permission
                if (item.superAdmin && !isSuperAdmin.value) {
                    return false;
                }

                // Super admins see all items
                if (isSuperAdmin.value) {
                    return true;
                }

                // Filter by yield/ltba publisher type for non-super-admins
                if (publisher.value && item.hideForYieldPublisher !== undefined) {
                    // hideForYieldPublisher=true means hide for yield publishers
                    // So we hide it when is_yield matches hideForYieldPublisher
                    if (item.hideForYieldPublisher === publisher.value.is_yield) {
                        return false;
                    }
                }

                return true;
            });

            mi = mi.map(routeParamsApplicator({ publisher: publisherId.value }));
            return mi;
        });

        const adsTxt = computed(() => {
            let data = store.getters[adstxtGetterTypes.getAdstxtValidationStatus];
            return data;
        });

        // Get all publishers to check if user has both types
        const publishers = computed(() => store.getters[publisherGetterTypes.getPublishers] || []);
        const hasLtbaPublishers = computed(() => publishers.value.some((p) => !p.is_yield));
        const hasYieldPublishers = computed(() => publishers.value.some((p) => p.is_yield === true));

        // Show toggle if user has both types of publishers
        const showDashboardToggle = computed(() => {
            return hasLtbaPublishers.value && hasYieldPublishers.value;
        });

        return {
            publisher,
            userRights,
            isSuperAdmin,
            accountMenuItems,
            mainMenuItems,
            adsTxt,
            showDashboardToggle,
            dashboardMode,
            isDashboard,
            isPerformax,
        };
    },
};
</script>

<style lang="scss" scoped>
.navigation {
    &-bar {
        position: fixed;
        display: flex;
        justify-content: space-between;
        align-items: center;
        font-weight: 600;
        letter-spacing: 1px;
        background-color: $blue;
        z-index: 99;
        min-height: 65px;

        span {
            color: $white;
            margin-left: 2rem;
            top: -1rem;
        }
    }

    &-menu {
        display: flex;

        &-item {
            padding: 1.5rem;
            color: $white;
            position: relative;
            cursor: pointer;
            text-transform: uppercase;

            &:hover,
            &:active,
            &:focus {
                color: $white;

                &:before {
                    background-color: $white;
                }
            }

            &:before {
                content: "";
                position: absolute;
                bottom: 0;
                left: 0;
                right: 0;
                border-top-left-radius: 2px;
                border-top-right-radius: 2px;
                height: 3px;
            }
        }

        &-item.router-link-active {
            -webkit-text-fill-color: transparent;
            //background: $green-gradient;
            background: $green-2;
            background-clip: text;

            &:before {
                //background: $green-gradient;
                background: $green-2;
            }
        }

        &-item.super-admin {
            &:after {
                content: "S";
                color: #fff;
                position: absolute;
                top: 20px;
                opacity: 0.4;
            }
        }

        &-item.label {
            &:after {
                position: static;
                top: auto;
                right: auto;
                opacity: 0.8;
            }
        }

        &-item.label.warning {
            &:after {
                content: "⚠";
                color: #ff0;
                -webkit-text-fill-color: #ff0;
            }
        }

        &-item.label.error {
            &:after {
                content: "⚠";
                color: #f00;
                -webkit-text-fill-color: #f00;
            }
        }
    }

    &-right {
        display: flex;

        &-item {
            margin: 0 1rem;
        }
    }

    &__user-icon {
        border-radius: 90%;
        width: 40px;
        height: 40px;
        border: 1px solid $white;
    }

    &-dropdown-icon {
        font-size: 2rem;
        color: $white;
    }
}

div.navigation-logo {
    padding: 1em 2em;

    img {
        height: 3em;
    }
}

.navbar-brand {
    & img {
        max-height: 1.5rem;
    }
}

body.dark {
    .navigation {
        &-bar {
            background-color: $light-black;
        }
    }
}

body.variant-performax {
    .navigation {
        &-bar {
            background-color: $white;
            border-bottom: 2px solid #f3f2f6;
            font-size: 0.8rem;

            @include --hd-viewport {
                font-size: 1rem;
            }

            span {
                color: $px-purple;
            }
        }

        &-menu {
            &-item {
                padding: 1.6rem 0;
                margin: 0 0.7rem;

                color: $px-purple;

                &:hover,
                &:active,
                &:focus {
                    color: $black;

                    &:before {
                        background-color: $black;
                    }
                }
            }

            &-item.super-admin {
                &:after {
                    color: #000;
                }
            }

            &-item.router-link-active {
                -webkit-text-fill-color: transparent;
                background: $px-red;
                background-clip: text;

                &:before {
                    background: $px-red;
                }
            }
        }

        &-dropdown-icon {
            color: $px-purple;
        }
    }

    div.navigation-logo {
        padding: 0 1rem;

        img {
            height: 3em;
        }
    }
}
</style>
